/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useMemo, useState } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';

import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import { Container } from '@components/screen/Layout';
import Button from '@components/form/Button';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import VipBadge from '@components/customer/VipBadge';
import ModalFull from '@components/modal/ModalFull';
import * as Layout from '@components/screen/Layout';
import StatusPicker from '@components/form/StatusPicker';
import SelectBox from '@components/form/SelectBox';
import ModalUpload from '@components/modal/ModalUpload';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';

import Colors from '@global/colors';
import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import { filterAdded, paramsReseted } from '@/store/params';
import { getStatus, hasNoAccess, exportCsvApi } from '@global/helpers';
import StatusBadge from '@components/global/StatusBadge';
import { MsB2b } from '@api/ms-b2b';
import { showPopup } from '@/store/modal';
import templateAddress from '@/assets/csv/template_customer_import_address';
import templatePic from '@/assets/csv/template_customer_import_pic';
import templateType from '@/assets/csv/template_customer_import_customer_type';
import { importCustomer } from '@api/ms-account';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCustomerListQuery,
  useGetCustomerCountQuery,
  useGetCustomerInfiniteListInfiniteQuery,
} from '@api/listApi';

const dimension = Dimensions.get('screen');

const img = require('@/assets/img/default/image-default.png');

// Columns marked `hide: true` are toggleable and hidden by default (Table's
// built-in show/hide) — matching the old SociollaTable "options" column, which
// inserted the optional columns right after Sales Team (not at the very end).
const tableStructure = {
  name: { head: 'Customer Name', body: 'name', type: 'slot', width: '20%' },
  sales_team_name: {
    head: 'Sales Team',
    body: 'sales_team_name',
    width: '15%',
  },
  phone: { head: 'Phone', body: 'phone', hide: true },
  email: { head: 'Email', body: 'email', hide: true },
  city_name: { head: 'City', body: 'city_name', hide: true },
  state_name: { head: 'State', body: 'state_name', hide: true },
  country_name: { head: 'Country', body: 'country_name', hide: true },
  tax_id: { head: 'Tax ID', body: 'tax_id', hide: true },
  market_code: { head: 'Market Type', body: 'market_code', width: '16.5%' },
  business_code: {
    head: 'Business Type',
    body: 'business_code',
    width: '16.5%',
  },
  status: { head: 'Status', body: 'status', type: 'status', width: '14%' },
  is_active: {
    head: 'is Active',
    body: 'is_active',
    type: 'status_active',
    width: '12%',
  },
};

// Home entry pre-filters to approved + active customers. Must be a STABLE
// reference — useListScreen value-keys presetFilter by identity, so a fresh
// object each render would re-dispatch every render (infinite loop).
const HOME_PRESET = { status: 'approved', is_active: true };

const CUSTOMER_STATUS_OPTIONS = [
  { name: 'Active', value: 'true' },
  { name: 'In Active', value: 'false' },
];

// Export-modal option lists (separate from the list filter).
const MARKET_TYPES_EXPORT = [
  { name: 'GT', value: 'gt' },
  { name: 'MT', value: 'MT' },
];
const APPROVAL_STATUS_EXPORT = [
  { name: 'Draft', value: 'draft' },
  { name: 'Need Approval', value: 'need_approval' },
  { name: 'Rejected', value: 'rejected' },
  { name: 'Approved', value: 'approved' },
  { name: 'All', value: 'all' },
];
const CUSTOMER_STATUS_EXPORT = [
  { name: 'Active', value: 'active' },
  { name: 'In Active', value: 'inactive' },
];

const EXPORT_ROLES = [
  'superadmin',
  'general',
  'sales_admin',
  'branch_head',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'distributor_manager',
  'vice_president',
  'general_manager_marketing',
  'chief_of_staff',
  'regional_distributor_manager',
];

// UI filter shape → API query filter (was HEADER_DEKSTOP + the finalParam strip):
//  - keyword unwrapped from the search box's { $regex }
//  - user.id / market_type.code / status pass through
//  - is_active coerced to Boolean
//  - business_type (multi) → market_type.business_type.code: { $in: [codes] }
const toApiFilter = (raw = {}) => {
  const filter = {};
  const kw = raw.keyword?.$regex ?? raw.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  ['user.id', 'market_type.code', 'status'].forEach((k) => {
    if (raw[k] !== undefined && raw[k] !== '' && raw[k] !== null) {
      filter[k] = raw[k];
    }
  });
  if (
    raw.is_active !== undefined &&
    raw.is_active !== '' &&
    raw.is_active !== null
  ) {
    filter.is_active = raw.is_active === true || raw.is_active === 'true';
  }
  const bt = raw['market_type.business_type.code'];
  if (Array.isArray(bt) ? bt.length : bt) {
    filter['market_type.business_type.code'] = Array.isArray(bt)
      ? { $in: bt.map((el) => el.value ?? el) }
      : bt;
  }
  return filter;
};

// Enrich raw customers for display (business description, flattened *_name
// fields). Recomputed when data or business types change — never triggers a
// refetch (unlike the old businessTypeList effect). Status stays raw so the
// shared StatusBadge maps its own label/color.
const generateRows = (data, businessTypeList) =>
  (data || []).map((cus) => {
    let businessCode = cus.market_type?.business_type?.code || '';
    const businessType = businessTypeList.find(
      (bt) => bt._id == cus.market_type?.business_type?.id,
    );
    if (businessType?.description) {
      businessCode = `${businessCode} (${businessType.description})`;
    }
    const image = Array.isArray(cus?.image) ? cus.image[0] : cus?.image;
    return {
      ...cus,
      id: cus._id,
      image,
      state_name: cus.state?.name || '',
      sales_team_name: cus.sales_team?.name || '',
      country_name: cus.country?.name || '',
      city_name: cus.city?.name || '',
      market_code: cus.market_type?.code || '',
      business_code: businessCode,
      tax_id: '',
    };
  });

function CustomerScreen(props) {
  // ── selectors ──
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const exportCustomerOption = useSelector(
    (state) => state.modal.exportCustomerOption,
  );
  const showCustomerExportFilter = useSelector(
    (state) => state.modal.showCustomerExportFilter,
  );
  const exportAllCustomer = useSelector(
    (state) => state.modal.exportAllCustomer,
  );
  const showImportModalCustomer = useSelector(
    (state) => state.modal.showImportModalCustomer,
  );
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [businessTypeList, setBusinessTypeList] = useState([]);
  const [marketTypeOptions, setMarketTypeOptions] = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [importOption, setImportOption] = useState('');
  const [filterExport, setFilterExport] = useState({
    market_type_code: '',
    is_active: '',
    approval: '',
  });
  const [draftFilter, setDraftFilter] = useState({});

  // ── derived ──
  const hasExportAccess = !hasNoAccess(EXPORT_ROLES);
  const hasImportAccess = !hasNoAccess(['superadmin', 'general']);
  const assignedOptions = auth.userId
    ? [{ name: 'Assigned to me', value: auth.userId }]
    : [];
  // Home entry pre-filters to approved + active customers (applied once).
  const presetFilter = props.route?.params?.isHome ? HOME_PRESET : undefined;

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCustomerListQuery,
    countHook: useGetCustomerCountQuery,
    infiniteHook: useGetCustomerInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
    presetFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return generateRows(raw, businessTypeList);
  }, [list.data, businessTypeList]);

  // ── handlers ──
  const callBackRowPress = ({ item }) => {
    if (!item || !item._id) {
      return false;
    }
    openChild('CustomerNavigator', {
      screen: 'CustomerDetailScreen',
      params: { id: item._id, name: item.name, prev_page: '', tab: '' },
    });
  };

  const onPressFilter = () => {
    dispatch(showPopup({ name: 'showFilter', value: 'customer-screen' }));
  };

  const onExport = async (type = 'all') => {
    try {
      dispatch(showPopup({ name: 'exportCustomerOption', value: false }));
      const payload = {};
      if (type == 'delivery_invoice') {
        dispatch(showPopup({ name: 'showCustomerExportFilter', value: false }));
        payload.export_type = 'delivery_invoice';
        if (filterExport.market_type_code.value) {
          payload.market_type_code = filterExport.market_type_code.value;
        }
        if (filterExport.is_active) {
          payload.is_active =
            filterExport.is_active.value == 'active' ? true : false;
        }
      } else {
        dispatch(showPopup({ name: 'exportAllCustomer', value: false }));
        payload.export_type = 'all';
        if (filterExport.is_active) {
          payload.is_active =
            filterExport.is_active.value == 'active' ? true : false;
        }
        if (filterExport.approval) {
          payload.status = filterExport.approval.value;
          if (payload.status == 'all') {
            delete payload.status;
          }
        }
      }
      await exportCsvApi('customers-details', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
    } catch (error) {
      console.log('error-export', error);
    } finally {
      setFilterExport({ market_type_code: '', is_active: '', approval: '' });
    }
  };

  const deliveryInvoiceExport = async () => {
    try {
      dispatch(showPopup({ name: 'exportCustomerOption', value: false }));
      dispatch(showPopup({ name: 'showCustomerExportFilter', value: true }));
    } catch (error) {
      console.log('error-popup', error);
    }
  };

  const customerAllExport = async () => {
    try {
      dispatch(showPopup({ name: 'exportCustomerOption', value: false }));
      dispatch(showPopup({ name: 'exportAllCustomer', value: true }));
    } catch (error) {
      console.log('error-popup', error);
    }
  };

  const handleImportOption = (opt) => {
    setImportOption(opt);
    setShowImportOptions(false);
    dispatch(showPopup({ name: 'showImportModalCustomer', value: true }));
  };

  const handleImport = async (fileUrl) => {
    try {
      await importCustomer(importOption, { url: fileUrl });
      Toast.show({
        text2: 'Import Customer is in progress',
        text1: 'Success',
        type: 'success',
      });
    } catch (error) {
      console.error('error', error);
      Toast.show({
        text2: 'Something Wrong, please try again.',
        text1: 'Failed',
        type: 'error',
      });
    }
    dispatch(showPopup({ name: 'showImportModalCustomer', value: false }));
    dispatch(showPopup({ name: 'isLoading', value: false }));
  };

  // ── mobile row presentational parts ──
  const ProfileImage = ({ image }) => (
    <Image source={image ? { uri: image } : img} style={styles.profileImage} />
  );

  const ProfileName = ({ name, customerType }) => (
    <SociollaText numberOfLines={2} style={[styles.name, { gap: 1 }]}>
      {customerType === 'b2b_vip' && <VipBadge />}
      {name?.length > 23 ? `${name.substring(0, 23)}...` : name || '-'}
    </SociollaText>
  );

  const DetailRow = ({ label, value, customStyle }) => (
    <View style={customStyle}>
      <SociollaText numberOfLines={2} style={styles.detail}>
        {label} {value || '-'}
      </SociollaText>
    </View>
  );

  const renderItem = ({ data, index }) => (
    <TouchableOpacity
      key={`list-customer-${index}`}
      testID={`customer-list-item-${index}`}
      style={styles.tableRow}
      onPress={() => callBackRowPress({ item: data, index })}>
      <View style={styles.container}>
        <View style={styles.profileContainer}>
          <View style={styles.profileSection}>
            <ProfileImage image={data?.image} />
            <ProfileName name={data?.name} customerType={data?.customer_type} />
          </View>
          <StatusBadge status={data?.status} />
        </View>
        <View style={styles.profileContainer}>
          <DetailRow
            label=""
            value={data?.sales_team_name}
            customStyle={{ marginLeft: 52 }}
          />
          <View style={[styles.separatorLine, { marginLeft: 40 }]} />
          <DetailRow
            label=""
            value={data?.is_active ? 'Active' : 'Not active'}
            customStyle={{ marginRight: 40 }}
          />
        </View>
        <View
          style={[styles.profileContainer, { marginTop: 3, paddingTop: 3 }]}>
          <DetailRow
            label="Market:"
            value={data?.market_code ? data?.market_code.toUpperCase() : ''}
            customStyle={{ marginLeft: 54 }}
          />
          <View style={[styles.separatorLine, { marginLeft: 36 }]} />
          <DetailRow
            label="Business:"
            value={data?.business_code}
            customStyle={{ marginRight: 38 }}
          />
        </View>
      </View>
    </TouchableOpacity>
  );

  // ── staged filter draft, seeded on open ──
  const [draftBusinessType, setDraftBusinessType] = useState('');
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftBusinessType(applied['market_type.business_type.code'] || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── effects ──
  // Load market/business types once — for the filter option lists + display
  // enrichment. This does NOT trigger the list fetch (that killed the dup calls).
  useEffect(() => {
    MsB2b.getMarketType().then((res) => {
      if (res.status !== 200) {
        return;
      }
      const hash = {};
      const btList = [];
      const businessTypes = [];
      const marketTypes = (res.data.data || []).map((x) => {
        (x.business_type || []).forEach((bt) => {
          if (!hash[bt._id]) {
            btList.push(bt);
            if (bt.is_active) {
              businessTypes.push({ label: bt.code, value: bt.code });
            }
            hash[bt._id] = bt;
          }
        });
        return { name: x.code, value: x.code };
      });
      setMarketTypeOptions(marketTypes);
      setBusinessTypeOptions(businessTypes);
      setBusinessTypeList(btList);
    });
  }, []);

  useEffect(() => {
    try {
      const menus = [
        { type: 'function', name: 'Filter', callback: () => onPressFilter() },
      ];
      if (hasExportAccess) {
        menus.unshift(
          {
            type: 'function',
            name: 'Export All',
            callback: () => onExport('all'),
          },
          {
            type: 'function',
            name: 'Export Delivery and Invoice',
            callback: () => deliveryInvoiceExport(),
          },
        );
      }
      if (hasImportAccess) {
        menus.push(
          {
            type: 'function',
            name: 'Import Address',
            callback: () => handleImportOption('address'),
          },
          {
            type: 'function',
            name: 'Import Internal PIC',
            callback: () => handleImportOption('pic'),
          },
          {
            type: 'function',
            name: 'Import Customer Type',
            callback: () => handleImportOption('customer_type'),
          },
        );
      }
      props.navigation.setOptions({
        headerRight: () => {
          return isWeb ? (
            <NotificationSettingButton {...props} />
          ) : (
            <MoreButton testID="customer-more-button" menu={menus} {...props} />
          );
        },
      });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasExportAccess, hasImportAccess]);

  // ── render ──
  const nameSlot = ({ data, index }) => (
    <SociollaText key={`name-${index}`} numberOfLines={2}>
      {data?.name || '-'}
      {data?.customer_type === 'b2b_vip' ? <VipBadge /> : false}
    </SociollaText>
  );

  return (
    <Container testID="customer-screen">
      {isWeb && (
        <Layout.Title title="Customers" style={{ zIndex: 9 }}>
          {hasImportAccess ? (
            <View style={{ flexDirection: 'row' }}>
              <Button
                testID="customer-import-btn"
                type="default"
                rounded={true}
                buttonStyle={{ width: 110, marginRight: 10 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.black }}>
                    Import
                  </SociollaText>
                }
                onPress={() => setShowImportOptions((prev) => !prev)}
              />
              {showImportOptions ? (
                <View
                  testID="customer-import-options"
                  style={styles.addOptions}>
                  <TouchableOpacity
                    testID="customer-import-address-btn"
                    activeOpacity={0.8}
                    style={styles.addOptionsItem}
                    onPress={() => handleImportOption('address')}>
                    <SociollaText>Import Address</SociollaText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="customer-import-pic-btn"
                    activeOpacity={0.8}
                    style={styles.addOptionsItem}
                    onPress={() => handleImportOption('pic')}>
                    <SociollaText>Update Internal PIC</SociollaText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="customer-import-customer-type-btn"
                    activeOpacity={0.8}
                    style={styles.addOptionsItem}
                    onPress={() => handleImportOption('customer_type')}>
                    <SociollaText>Import Customer Type</SociollaText>
                  </TouchableOpacity>
                </View>
              ) : (
                false
              )}
            </View>
          ) : (
            false
          )}
        </Layout.Title>
      )}

      {isWeb ? (
        <Table
          testID="customer-web-table"
          filterName={scope}
          name="customer-screen"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search..."
          searchValue={searchValue}
          showSearch={true}
          showFilter={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => callBackRowPress({ item: data })}
          moreInlineFilter={
            hasExportAccess && (
              <Button
                testID="customer-export-btn"
                type="primary"
                rounded={true}
                buttonStyle={{ width: 90, marginLeft: 10 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    Export
                  </SociollaText>
                }
                onPress={() =>
                  dispatch(
                    showPopup({ name: 'exportCustomerOption', value: true }),
                  )
                }
              />
            )
          }
          slot={{ name: nameSlot }}
        />
      ) : (
        <TableMobile
          testID="customer-mobile-table"
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by name, customer code..."
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          headerComponent={<></>}
          renderItem={renderItem}
        />
      )}

      {/* ── Export options ── */}
      <ModalFull
        testID="customer-export-options"
        title="Export Customer Options"
        noTranslate={true}
        show={exportCustomerOption}
        maxWidth={isWeb && !isMobileWeb ? 500 : '100%'}
        onClose={() =>
          dispatch(showPopup({ name: 'exportCustomerOption', value: false }))
        }>
        <View
          testID="customer-export-options-content"
          style={styles.userCreate}>
          <TouchableOpacity
            testID="customer-export-all-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => customerAllExport()}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              Export All Customers
            </SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            testID="customer-export-delivery-invoice-btn"
            activeOpacity={0.6}
            style={styles.itemOption}
            onPress={() => deliveryInvoiceExport()}>
            <Image
              style={{ width: 35, height: 43 }}
              width={35}
              height={43}
              source={require('@assets/img/icons/add-from-csv.png')}
            />
            <SociollaText style={styles.itemText}>
              Export Delivery and Invoice Customer
            </SociollaText>
          </TouchableOpacity>
        </View>
      </ModalFull>

      {/* ── Export all ── */}
      <ModalFull
        testID="customer-export-all"
        title="Export All Customer"
        noTranslate={true}
        show={exportAllCustomer}
        maxWidth={isWeb && !isMobileWeb ? 500 : '100%'}
        onClose={() =>
          dispatch(showPopup({ name: 'exportAllCustomer', value: false }))
        }>
        <View style={styles.modalWrapper}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup>
                <StatusPicker
                  testID="customer-export-all-approval-status"
                  label="Approval Status"
                  options={APPROVAL_STATUS_EXPORT}
                  value={filterExport.approval}
                  trackBy="value"
                  labelKey="name"
                  minWidth={30}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, approval: val })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup>
                <StatusPicker
                  testID="customer-export-all-customer-status"
                  label="Customers Status"
                  options={CUSTOMER_STATUS_EXPORT}
                  value={filterExport.is_active}
                  trackBy="value"
                  labelKey="name"
                  minWidth={70}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, is_active: val })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View style={styles.buttonWrapper}>
            <Button
              testID="customer-export-all-submit-btn"
              rounded={true}
              type="primary"
              buttonStyle={styles.buttonExport}
              disabledStyle={{ width: 90 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => onExport('all')}
            />
          </View>
        </View>
      </ModalFull>

      {/* ── Export delivery & invoice ── */}
      <ModalFull
        testID="customer-export-delivery-invoice"
        title="Export Delivery and Invoice customer"
        noTranslate={true}
        show={showCustomerExportFilter}
        onClose={() =>
          dispatch(
            showPopup({ name: 'showCustomerExportFilter', value: false }),
          )
        }>
        <View style={styles.modalWrapper}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup>
                <StatusPicker
                  testID="customer-export-delivery-invoice-market-type"
                  label="Market Type"
                  options={MARKET_TYPES_EXPORT}
                  value={filterExport.market_type_code}
                  trackBy="value"
                  labelKey="name"
                  minWidth={30}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, market_type_code: val })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
            <Layout.FormWrapper style={{ padding: 15 }}>
              <Layout.FormGroup>
                <StatusPicker
                  testID="customer-export-delivery-invoice-customer-status"
                  label="Customers Status"
                  options={CUSTOMER_STATUS_EXPORT}
                  value={filterExport.is_active}
                  trackBy="value"
                  labelKey="name"
                  minWidth={70}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, is_active: val })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View style={styles.buttonWrapper}>
            <Button
              testID="customer-export-delivery-invoice-submit-btn"
              rounded={true}
              type="primary"
              buttonStyle={styles.buttonExport}
              disabledStyle={{ width: 90 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => onExport('delivery_invoice')}
            />
          </View>
        </View>
      </ModalFull>

      {/* ── Import ── */}
      <ModalUpload
        testID="customer-import"
        show={showImportModalCustomer}
        onClose={() =>
          dispatch(showPopup({ name: 'showImportModalCustomer', value: false }))
        }
        templateUrl={
          {
            address: templateAddress,
            pic: templatePic,
            customer_type: templateType,
          }[importOption]
        }
        onUpload={handleImport}
        autoUploadS3={true}
        name={importOption}
        validHeader={
          {
            address: [
              'cerebro_id',
              'address_type',
              'address_name',
              'address_identification',
              'address_contact_person',
              'address_street',
              'address_street2',
              'address_postal_code',
              'address_phone',
              'address_country_cerebro_id',
              'address_state_cerebro_id',
              'address_city',
              'address_district',
              'address_email',
              'address_mobile',
              'address_notes',
              'store_code',
              'address_id',
              'address_is_premium',
            ],
            pic: ['action', 'customer_code', 'user'],
            customer_type: ['_id', 'customer_type_id'],
          }[importOption]
        }
      />

      {/* ── List filter (staged) ── */}
      <Layout.Filter
        name="customer-screen"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftBusinessType('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        {assignedOptions.length ? (
          <Layout.FilterItem>
            <SociollaText style={styles.filterLabel}>Assigned To</SociollaText>
            <StatusPicker
              label=""
              options={assignedOptions}
              value={draftFilter['user.id']}
              trackBy="value"
              labelKey="name"
              minWidth={80}
              onChange={(val) =>
                setDraftFilter((f) => ({ ...f, 'user.id': val.value }))
              }
            />
          </Layout.FilterItem>
        ) : (
          false
        )}
        <Layout.FilterItem>
          <SociollaText style={styles.filterLabel}>Market Type</SociollaText>
          <StatusPicker
            label=""
            options={marketTypeOptions}
            value={draftFilter['market_type.code']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, 'market_type.code': val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={styles.filterLabel}>Business Type</SociollaText>
          <SelectBox
            editable={true}
            hideLabel
            options={businessTypeOptions}
            multiple={true}
            value={draftBusinessType}
            placeholder="Select Business Type"
            trackBy="value"
            labelKey="label"
            formLabelKey="label"
            onChange={(value) => {
              setDraftBusinessType(value);
              setDraftFilter((f) => ({
                ...f,
                'market_type.business_type.code': value,
              }));
            }}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={styles.filterLabel}>Status</SociollaText>
          <StatusPicker
            label=""
            options={getStatus('customer')}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="label"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <SociollaText style={styles.filterLabel}>
            Customers Status
          </SociollaText>
          <StatusPicker
            label=""
            options={CUSTOMER_STATUS_OPTIONS}
            value={
              draftFilter.is_active === undefined
                ? ''
                : String(draftFilter.is_active)
            }
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, is_active: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Container>
  );
}

const styles = StyleSheet.create({
  modalWrapper: {
    height: isWeb
      ? 'calc(100vh - 150px)'
      : dimension.height - (isAndroid ? 140 : 110),
  },
  buttonWrapper: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: isWeb ? 20 : 15,
    paddingHorizontal: isWeb ? 20 : 15,
    backgroundColor: Colors.white,
    paddingBottom: isMobileWeb
      ? 'calc(15px + env(safe-area-inset-bottom))'
      : 15,
    ...(isMobileWeb ? { position: 'fixed', bottom: 0, right: 0, left: 0 } : {}),
  },
  buttonExport: {
    width: isWeb ? 100 : dimension.width / 2 - (isMobileWeb ? 20 : 25),
  },
  filterLabel: { fontSize: 13, marginBottom: 10 },
  tableRow: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    height: '100%',
    minHeight: 120,
    width: '100%',
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 20,
    flex: 1,
  },
  userCreate: {
    width: '100%',
    paddingHorizontal: isWeb ? 25 : 15,
    paddingTop: isWeb ? 25 : 15,
    paddingBottom: isWeb ? 40 : 15,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  itemOption: {
    padding: 15,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginHorizontal: isWeb ? 15 : 10,
  },
  itemText: { fontSize: 14, marginTop: 15, width: 150 },
  container: {
    marginTop: 15,
    marginBottom: 10,
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: '#fff',
    borderRadius: 10,
    width: '100%',
  },
  profileContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  profileImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
    borderColor: Colors.grey0,
    borderWidth: 2,
    resizeMode: 'contain',
    aspectRatio: 1,
  },
  name: { fontSize: 13, fontWeight: 'bold', maxWidth: 140, marginBottom: 15 },
  detail: { fontSize: 10, color: '#666', width: 90 },
  profileSection: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  separatorLine: {
    borderWidth: 0.5,
    width: 0.5,
    height: 10,
    borderColor: '#BDC1C9',
  },
  addOptions: {
    position: 'absolute',
    width: 180,
    top: 50,
    left: -60,
    zIndex: 9,
    elevation: 1,
    backgroundColor: Colors.white,
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    ...(!isWeb ? { position: 'absolute', top: 0, left: 100 } : {}),
  },
  addOptionsItem: {
    paddingVertical: 13,
    paddingHorizontal: 15,
    ...(!isWeb
      ? { borderBottomColor: Colors.grey0, borderBottomWidth: 1 }
      : {}),
  },
});

export default CustomerScreen;
